import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

// The three answers the product can be drawn by, carrying the words each is offered in, so the list
// exists once rather than once per place a control is drawn. Light leads deliberately: it is what
// the product chooses for somebody who has never said, and putting the device first would present
// the operating system as the primary answer when it is the fallback.
export const THEMES = [
  { id: "light", label: "Light" },
  { id: "dark", label: "Dark" },
  { id: "system", label: "Match this device" },
] as const;

export type Theme = (typeof THEMES)[number]["id"];

// Appearance and palette are deliberately separate choices. Appearance decides whether the product
// is drawn light or dark; palette decides which set of colors that appearance is drawn from. Fold
// them into one list and every palette costs two entries, somebody who prefers dark has to give it
// up to try a palette, and the list grows by two every time a color is proposed.
export const PALETTES = [
  { id: "tidal", label: "Tidal" },
  { id: "deepwater", label: "Deep water" },
  { id: "warm-sand", label: "Warm sand" },
  { id: "amber", label: "Amber hearth" },
  { id: "olive", label: "Olive grove" },
  { id: "vineyard", label: "Vineyard" },
  { id: "iris", label: "Iris" },
] as const;

export type Palette = (typeof PALETTES)[number]["id"];

// What somebody who has never said gets, in both dimensions. Named here so the answer is given once
// rather than spelled into every place that has to fall back to it.
export const DEFAULT_THEME: Theme = "light";
// Tidal, because the default should be drawn from the same water as the mark beside the
// product name. Deep water is the second brand candidate under comparison, and warm sand,
// the previous default, stays exactly where it was for anyone who chose it.
export const DEFAULT_PALETTE: Palette = "tidal";

const themeIds: readonly string[] = THEMES.map((option) => option.id);
const paletteIds: readonly string[] = PALETTES.map((option) => option.id);

// An appearance arrives from browser storage or from an account, and both outlive the release that
// wrote them: a palette can be renamed or withdrawn while the value naming it sits in a database
// row or a storage key for another year. Checking rather than casting is what stops that value
// being written onto the document as an attribute matching no block, which would leave somebody on
// the default colors while every control insisted their choice was the one showing.
export function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && themeIds.includes(value);
}

export function isPalette(value: unknown): value is Palette {
  return typeof value === "string" && paletteIds.includes(value);
}

interface ThemeContextValue {
  theme: Theme;
  resolved: "light" | "dark";
  palette: Palette;
  // One way in, on purpose. Putting an appearance into effect and recording it against a person are
  // two different acts, and this is only the first of them: showing somebody what their account
  // already holds must never be mistakable for them choosing it again, which is exactly what would
  // happen if applying a value also saved it. Whoever calls this decides separately whether anything
  // is worth writing down, and the pair moves together because a theme drawn from last palette's
  // colors is a frame nobody asked to see.
  apply(theme: Theme, palette: Palette): void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

// The browser chrome around the page (the address bar on a phone, the title bar of an installed
// app) takes its color from the theme-color meta. theme-boot.js sets it to the default palette's
// canvas for the resolved scheme before the stylesheet exists; once the stylesheet is applied, the
// canvas of whichever palette and scheme is showing is the right answer, and it is read from the
// stylesheet rather than repeated here so the two cannot drift. A test document has no stylesheet
// and no computed value, and is left alone.
function paintThemeColor(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) return;
  const canvas = getComputedStyle(document.documentElement).getPropertyValue("--bg").trim();
  if (canvas) meta.content = canvas;
}

// A browser can refuse storage outright: private modes, blocked third-party contexts,
// and quota failures throw on access rather than returning null, and some runtimes
// expose the name without the API. Appearance is a preference, not a requirement, so a
// refusal falls back to the product default instead of taking the application down.
function readStoredTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem("venueops-theme");
    return isTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredTheme(theme: Theme): void {
  try {
    window.localStorage.setItem("venueops-theme", theme);
  } catch {
    // The choice still applies to this session; it just will not survive a reload.
  }
}

function readStoredPalette(): Palette | null {
  try {
    const stored = window.localStorage.getItem("venueops-palette");
    return isPalette(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredPalette(palette: Palette): void {
  try {
    window.localStorage.setItem("venueops-palette", palette);
  } catch {
    // The choice still applies to this session; it just will not survive a reload.
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme() ?? DEFAULT_THEME);
  const [palette, setPaletteState] = useState<Palette>(
    () => readStoredPalette() ?? DEFAULT_PALETTE,
  );
  const [resolved, setResolved] = useState<"light" | "dark">(() => resolveTheme(theme));

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const next = resolveTheme(theme);
      setResolved(next);
      document.documentElement.dataset.theme = next;
      document.documentElement.style.colorScheme = next;
      paintThemeColor();
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [theme]);

  // Warm sand, the palette the product started with, has no block of its own: it is the plain
  // [data-theme] pair that every other palette overrides. The attribute is written whichever palette
  // is showing, so the document always states which one it is instead of leaving that palette to be
  // inferred from an absence.
  useEffect(() => {
    document.documentElement.dataset.palette = palette;
    paintThemeColor();
  }, [palette]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolved,
      palette,
      apply(nextTheme, nextPalette) {
        writeStoredTheme(nextTheme);
        writeStoredPalette(nextPalette);
        setThemeState(nextTheme);
        setPaletteState(nextPalette);
      },
    }),
    [theme, resolved, palette],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be used inside ThemeProvider");
  return value;
}
